import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { NbCard, NbButton, Chip } from "@/components/nb";
import { Stagger, Item } from "@/components/motion";
import { Play, MessageCircle, Target, X, Sparkles, CheckCircle2, Circle, BookmarkPlus, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";

export default function StudyPlan({ subject, level, pct, weak_topics, initialPlan }) {
  const [plan, setPlan] = useState(initialPlan || null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openVideo, setOpenVideo] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/study/plan/generate", { subject, level, pct, weak_topics });
      setPlan(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI plan failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const savePlan = async () => {
    if (!user) return toast.error("Sign in to save your plan");
    if (!plan) return;
    setSaving(true);
    try {
      const r = await api.post(`/study/plans/${plan.id}/save`);
      setPlan(r.data);
      toast.success("Plan saved. Tick tasks as you go!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const toggleActivity = async (day, index, completed) => {
    if (!plan?.saved) return; // only allow ticking on saved plan
    // optimistic
    const key = `${day}-${index}`;
    setPlan((p) => ({ ...p, progress: { ...(p.progress || {}), [key]: completed } }));
    try {
      const r = await api.patch(`/study/plans/${plan.id}/progress`, { day, index, completed });
      setPlan(r.data);
    } catch {
      toast.error("Could not save progress");
      // revert
      setPlan((p) => ({ ...p, progress: { ...(p.progress || {}), [key]: !completed } }));
    }
  };

  const askInChat = (prompt, subj) => {
    sessionStorage.setItem("sl_chat_prefill", JSON.stringify({ prompt, subject: subj || "General" }));
    nav("/study/chat");
  };

  const totals = useMemo(() => {
    if (!plan) return { done: 0, all: 0, pct: 0 };
    let all = 0, done = 0;
    plan.days.forEach((d) => {
      d.activities.forEach((_, idx) => {
        all += 1;
        if (plan.progress?.[`${d.day}-${idx}`]) done += 1;
      });
    });
    return { done, all, pct: all ? Math.round(100 * done / all) : 0 };
  }, [plan]);

  if (!plan) {
    return (
      <NbCard color="tertiary" className="!p-6 mt-8" data-testid="plan-cta">
        <div className="flex items-center gap-4 flex-wrap">
          <Sparkles className="w-8 h-8" />
          <div className="flex-1 min-w-[220px]">
            <h3 className="font-heading font-black text-2xl tracking-tight">Turn this score into wins.</h3>
            <p className="text-neutral-800">A personalised 3-day study plan with videos, chat starters and practice tasks.</p>
          </div>
          <NbButton onClick={generate} color="black" disabled={loading} data-testid="gen-plan">
            {loading ? "Building plan..." : "Get my 3-day plan →"}
          </NbButton>
        </div>
      </NbCard>
    );
  }

  const dayColors = ["primary", "secondary", "accent"];
  const isDone = (day, idx) => !!plan.progress?.[`${day}-${idx}`];

  return (
    <div className="mt-8" data-testid="study-plan">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mb-4 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <Chip color="tertiary">3-DAY PLAN</Chip>
            {plan.saved && <Chip color="primary">SAVED</Chip>}
          </div>
          <h3 className="font-heading font-black text-3xl tracking-tighter mt-2">{plan.title}</h3>
          <p className="text-neutral-800 italic mt-1">"{plan.summary}"</p>
          {plan.saved && (
            <div className="mt-3 flex items-center gap-3">
              <div className="flex-1 max-w-md h-3 bg-white nb-border rounded-full overflow-hidden">
                <motion.div className="h-full bg-sl-primary" initial={{ width: 0 }} animate={{ width: `${totals.pct}%` }} transition={{ duration: 0.4 }} />
              </div>
              <span className="font-mono-sl text-xs uppercase" data-testid="plan-progress">{totals.done}/{totals.all} · {totals.pct}%</span>
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {!plan.saved ? (
            <NbButton onClick={savePlan} color="black" disabled={saving} data-testid="save-plan"><BookmarkPlus className="w-4 h-4 inline-block mr-1" />{saving ? "Saving..." : "Save plan"}</NbButton>
          ) : (
            <NbButton onClick={() => nav("/study/plans")} color="white" data-testid="view-plans"><BookmarkCheck className="w-4 h-4 inline-block mr-1" /> My plans</NbButton>
          )}
        </div>
      </motion.div>

      <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-5" delay={0.1}>
        {plan.days.map((d, i) => (
          <Item key={d.day}>
            <NbCard color={dayColors[i % 3]} className="!p-5 h-full flex flex-col" data-testid={`plan-day-${d.day}`}>
              <div className="flex items-center justify-between">
                <Chip color="tertiary">DAY {d.day}</Chip>
                <span className="font-mono-sl text-[10px] uppercase">{d.activities.length} tasks</span>
              </div>
              <h4 className="font-heading font-black text-xl tracking-tight mt-2">{d.title}</h4>
              {d.focus && <div className="font-mono-sl text-[10px] uppercase text-neutral-700 mt-1">FOCUS · {d.focus}</div>}

              <div className="mt-4 space-y-2">
                {d.activities.map((a, j) => {
                  const done = isDone(d.day, j);
                  const CheckIcon = done ? CheckCircle2 : Circle;
                  const checkBtn = plan.saved ? (
                    <button onClick={(e) => { e.stopPropagation(); toggleActivity(d.day, j, !done); }} data-testid={`plan-check-${d.day}-${j}`} className="shrink-0" aria-label="toggle done">
                      <CheckIcon className={`w-5 h-5 ${done ? "text-green-700" : "text-neutral-500"}`} />
                    </button>
                  ) : null;

                  const wrapper = "w-full text-left bg-white nb-border rounded-lg px-3 py-2 flex items-center gap-3 nb-hover";
                  const strike = done ? "line-through opacity-60" : "";

                  if (a.type === "video") {
                    const body = (
                      <>
                        {checkBtn}
                        <Play className="w-4 h-4 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-mono-sl text-[9px] uppercase text-neutral-600">{a.search_url ? "SEARCH YOUTUBE" : "VIDEO"}</div>
                          <div className={`text-sm font-semibold truncate ${strike}`}>{a.title || a.query}</div>
                        </div>
                      </>
                    );
                    return a.search_url ? (
                      <div key={j} className={wrapper} data-testid={`plan-video-${d.day}-${j}`}>
                        {checkBtn}
                        <a href={a.search_url} target="_blank" rel="noreferrer" className="flex items-center gap-3 flex-1 min-w-0">
                          <Play className="w-4 h-4 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-mono-sl text-[9px] uppercase text-neutral-600">SEARCH YOUTUBE</div>
                            <div className={`text-sm font-semibold truncate ${strike}`}>{a.query}</div>
                          </div>
                        </a>
                      </div>
                    ) : (
                      <div key={j} className={wrapper} data-testid={`plan-video-${d.day}-${j}`}>
                        {checkBtn}
                        <button onClick={() => setOpenVideo(a)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                          <Play className="w-4 h-4 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-mono-sl text-[9px] uppercase text-neutral-600">VIDEO</div>
                            <div className={`text-sm font-semibold truncate ${strike}`}>{a.title}</div>
                          </div>
                        </button>
                      </div>
                    );
                  }
                  if (a.type === "chat") {
                    return (
                      <div key={j} className={wrapper} data-testid={`plan-chat-${d.day}-${j}`}>
                        {checkBtn}
                        <button onClick={() => askInChat(a.prompt, a.subject)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                          <MessageCircle className="w-4 h-4 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-mono-sl text-[9px] uppercase text-neutral-600">ASK DOUBT-BUDDY</div>
                            <div className={`text-sm italic truncate ${strike}`}>"{a.prompt}"</div>
                          </div>
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div key={j} className={wrapper} data-testid={`plan-practice-${d.day}-${j}`}>
                      {checkBtn}
                      <Target className="w-4 h-4 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono-sl text-[9px] uppercase text-neutral-600">PRACTICE</div>
                        <div className={`text-sm ${strike}`}>{a.title}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </NbCard>
          </Item>
        ))}
      </Stagger>

      {openVideo && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-5" onClick={() => setOpenVideo(null)} data-testid="plan-video-modal">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-4xl bg-white nb-border nb-shadow-xl rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b-2 border-black flex justify-between items-center">
              <div className="font-heading font-bold truncate">{openVideo.title}</div>
              <button onClick={() => setOpenVideo(null)} data-testid="close-plan-video" className="p-2 nb-border rounded-lg bg-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="aspect-video bg-black">
              <iframe title={openVideo.title} className="w-full h-full" src={`https://www.youtube.com/embed/${openVideo.youtube_id}?rel=0&autoplay=1`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
